import {
  AggregationGroupBy,
  LogTemplateType,
  LogsTemplate,
  SectionUnitsEnum,
  UnitSystemEnum,
} from '@/__generated__/graphql';
import { WorklogsTemplate } from './types';
import convert from 'convert';
import { roundNumber } from '@/utils/unitSystem';

export type ConvertValueByUnitOptions = {
  /** Decimal places when converting area for display (`view`); defaults to 3 if omitted. */
  viewRoundDecimals?: number | null;
};

/**
 * Converts a form value to the appropriate number format based on section unit type
 */
export const convertValueByUnit = (
  value: number | '',
  sectionUnit: SectionUnitsEnum | null | undefined,
  unitSystem: UnitSystemEnum,
  operation: 'view' | 'update',
  options?: ConvertValueByUnitOptions
): number => {
  const numVal = Number(value);

  const getConvertedArea = (): number => {
    // At the time of saving values, we always save in Metric units
    // This line checks if the current unit system is Imperial, if so, we convert the value to Metric
    // to keep consistency in the database
    if (operation === 'update') {
      return unitSystem === UnitSystemEnum.Imperial
        ? convert(numVal, 'square feet').to('square meter')
        : numVal;
    }

    const viewDecimals = options?.viewRoundDecimals ?? 3;

    // At the time of viewing values, we convert from Metric to Imperial if required
    // Also, round numbers when viewing, and NOT when updating
    return roundNumber(
      unitSystem === UnitSystemEnum.Metric
        ? numVal
        : convert(numVal, 'square meter').to('square feet'),
      viewDecimals
    );
  };

  switch (sectionUnit) {
    case SectionUnitsEnum.Usd:
    case SectionUnitsEnum.Gbp:
    case SectionUnitsEnum.Hours:
      return numVal;
    case SectionUnitsEnum.Area:
      return getConvertedArea();
    default:
      return Number.isFinite(numVal) ? roundNumber(numVal, 3) : numVal;
  }
};

/**
 * Finds the section unit for a given field key
 */
export const getSectionUnitForField = (
  fieldKey: string,
  sections: WorklogsTemplate['sections'] | undefined
): SectionUnitsEnum | null => {
  if (!sections) return null;

  const section = sections.find((s) => s.fieldKeys.includes(fieldKey));
  return section?.sectionUnit ?? null;
};
/**
 * Converts stored total POB hours back to individual hours for form display.
 * Returns the stored value unchanged if POB is not positive (avoids division by zero).
 */
export const toIndividualHours = (totalPobHours: number, pob: number): number => {
  if (pob <= 0) return totalPobHours;
  return roundNumber(totalPobHours / pob, 4);
};

/**
 * Converts individual hours to total POB hours for storage.
 * Returns the individual hours unchanged if POB is not positive.
 */
export const toTotalPobHours = (individualHours: number, pob: number): number => {
  return pob > 0 ? roundNumber(individualHours * pob, 4) : individualHours;
};

/**
 * Gets the logged value for a field from workpack logs
 */
export const getLoggedValueForField = (
  fieldKey: string | null | undefined,
  logs: Array<{ fieldKey: string; total: number }> | undefined
): number => {
  if (!fieldKey || !logs) return 0;
  return logs.find((item) => item.fieldKey === fieldKey)?.total ?? 0;
};

export const getSectionUnitSymbol = (
  unit: SectionUnitsEnum | null | undefined,
  unitSystem: UnitSystemEnum
) => {
  switch (unit) {
    case SectionUnitsEnum.Area:
      return unitSystem === UnitSystemEnum.Metric ? 'm²' : 'ft²';
    case SectionUnitsEnum.Gbp:
      return '€';
    case SectionUnitsEnum.Usd:
      return '$';
    case SectionUnitsEnum.Hours:
      return 'h';
    case SectionUnitsEnum.DegreeFahrenheit:
      return '°F';
    case SectionUnitsEnum.DegreeCelsius:
      return '°C';
    case SectionUnitsEnum.Mils:
      return 'mils';
    case SectionUnitsEnum.Ppm:
      return 'ppm';
    case SectionUnitsEnum.Percentage:
      return '%';
    case SectionUnitsEnum.Na:
      return '';
    default:
      return '';
  }
};

export const getSectionUnitLabel = (unit: SectionUnitsEnum | null | undefined) => {
  switch (unit) {
    case SectionUnitsEnum.Area:
      return 'Area';
    case SectionUnitsEnum.Gbp:
    case SectionUnitsEnum.Usd:
      return 'Cost';
    case SectionUnitsEnum.Hours:
      return 'Hours';
    case SectionUnitsEnum.DegreeFahrenheit:
      return '°F';
    case SectionUnitsEnum.DegreeCelsius:
      return '°C';
    case SectionUnitsEnum.Mils:
      return 'mils';
    case SectionUnitsEnum.Ppm:
      return 'ppm';
    case SectionUnitsEnum.Percentage:
      return '%';
    default:
      return '';
  }
};

/**
 * Formats a number with appropriate suffix (k, M, B, etc.)
 * @param value - The numeric value to format
 * @param isCurrency - If true, uses currency formatting (M, B, T) starting from millions. If false, uses k, M, B starting from thousands.
 * @returns Formatted string with suffix
 */
const formatNumberWithSuffix = (value: number, isCurrency: boolean = false): string => {
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (isCurrency) {
    // Currency formatting: M (millions), B (billions), T (trillions)
    if (absValue >= 1_000_000_000_000) {
      return `${sign}${(absValue / 1_000_000_000_000).toFixed(1)}T`;
    }
    if (absValue >= 1_000_000_000) {
      return `${sign}${(absValue / 1_000_000_000).toFixed(1)}B`;
    }
    if (absValue >= 1_000_000) {
      return `${sign}${(absValue / 1_000_000).toFixed(1)}M`;
    }
    return value.toString();
  } else {
    // General formatting: k (thousands), M (millions), B (billions)
    if (absValue >= 1_000_000_000) {
      return `${sign}${(absValue / 1_000_000_000).toFixed(1)}B`;
    }
    if (absValue >= 1_000_000) {
      return `${sign}${(absValue / 1_000_000).toFixed(1)}M`;
    }
    if (absValue >= 1_000) {
      return `${sign}${(absValue / 1_000).toFixed(1)}k`;
    }
    return value.toString();
  }
};

export const getDisplayValue = (
  value: string | number,
  sectionUnit: SectionUnitsEnum | null | undefined,
  unitSystem: UnitSystemEnum,
  roundToDecimal?: number | null
): string | number => {
  const numericValue = Number(value);

  // If no sectionUnit but value is numeric, apply general suffix formatting as fallback
  if (!sectionUnit && Number.isFinite(numericValue)) {
    const roundedValue = roundToDecimal ? roundNumber(numericValue, roundToDecimal) : numericValue;
    return formatNumberWithSuffix(roundedValue, false);
  }

  // If value is a string and no unit, return as-is
  if (typeof value === 'string') {
    return value;
  }

  const isArea = sectionUnit === SectionUnitsEnum.Area;
  const areaViewDecimals = roundToDecimal ?? 3;
  const convertedValue = convertValueByUnit(
    value,
    sectionUnit,
    unitSystem,
    'view',
    isArea ? { viewRoundDecimals: areaViewDecimals } : undefined
  );
  const finalValue =
    isArea || roundToDecimal === undefined || roundToDecimal === null
      ? convertedValue
      : roundNumber(convertedValue, roundToDecimal);

  // Apply suffix formatting for specific unit types
  if (sectionUnit === SectionUnitsEnum.Hours || sectionUnit === SectionUnitsEnum.Area) {
    if (Number.isFinite(finalValue)) {
      return formatNumberWithSuffix(finalValue, false);
    }
  }

  if (sectionUnit === SectionUnitsEnum.Usd || sectionUnit === SectionUnitsEnum.Gbp) {
    if (Number.isFinite(finalValue)) {
      return formatNumberWithSuffix(finalValue, true);
    }
  }

  return finalValue;
};
export const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Normalizes API date strings or Dates to `yyyy-mm-dd` for version / log filters. */
export const toKpiLogDateFilterString = (
  value: string | Date | undefined | null
): string | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'string') {
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
    if (match) return match[1];
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return formatDateToYYYYMMDD(parsed);
    return undefined;
  }
  if (value instanceof Date) return formatDateToYYYYMMDD(value);
  return undefined;
};

/**
 * Compares two dates as calendar dates (year-month-day) ignoring time and timezone.
 * This is useful when comparing dates stored in UTC with dates in local timezone.
 *
 * The function normalizes both dates to UTC calendar dates for consistent comparison.
 * This ensures that a comment saved for "March 8th" (stored as 2026-03-08T00:00:00.000+00:00)
 * matches when a user selects "March 8th" in their local timezone, regardless of timezone differences.
 *
 * The key insight is that when a date string like "2026-03-08" is stored and later converted
 * to a Date object, it represents a calendar date, not a specific moment in time. We compare
 * the calendar dates by normalizing both to UTC.
 *
 * @param date1 - First date to compare (typically a UTC date from metadata like "2026-03-08T00:00:00.000+00:00")
 * @param date2 - Second date to compare (typically a local timezone date selected by the user)
 * @returns true if both dates represent the same calendar day when normalized to UTC
 */
export const isSameCalendarDay = (date1: Date, date2: Date): boolean => {
  // Normalize both dates to UTC calendar dates (YYYY-MM-DD format)
  // This ensures consistent comparison regardless of timezone
  const date1Str = `${date1.getUTCFullYear()}-${String(date1.getUTCMonth() + 1).padStart(
    2,
    '0'
  )}-${String(date1.getUTCDate()).padStart(2, '0')}`;
  const date2Str = `${date2.getUTCFullYear()}-${String(date2.getUTCMonth() + 1).padStart(
    2,
    '0'
  )}-${String(date2.getUTCDate()).padStart(2, '0')}`;

  return date1Str === date2Str;
};

const localDateFromYMD = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const localDateFromYMStart = (dateStr: string): Date => {
  const [year, month] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, 1);
};

export const formatDate = (
  date: Date,
  _dayFormat?: any,
  _monthFormat?: any,
  _yearFormat?: any,
  timeZone?: string
): string => {
  const d = timeZone ? new Date(date.toLocaleString('en-US', { timeZone })) : date;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

export const formatLogDate = (
  dateStr: string,
  intervalRange: AggregationGroupBy,
  startDate?: string,
  endDate?: string,
  userTimezone?: string
): { dayOfWeek: string; formattedDate: string } => {
  switch (intervalRange) {
    case AggregationGroupBy.Daily: {
      const date = localDateFromYMD(startDate ?? dateStr);

      const dayOfWeek = date.toLocaleDateString('en-GB', {
        weekday: 'short',
        ...(userTimezone && { timeZone: userTimezone }),
      });

      return {
        dayOfWeek,
        formattedDate: formatDate(date, 'numeric', 'short', 'numeric', userTimezone),
      };
    }

    case AggregationGroupBy.Weekly: {
      const [, week] = dateStr.split('-W');
      const weekNum = Number.parseInt(week, 10);

      const startFormatted = formatDate(
        localDateFromYMD(startDate!),
        'numeric',
        'short',
        'numeric',
        userTimezone
      );

      const endFormatted = formatDate(
        localDateFromYMD(endDate!),
        'numeric',
        'short',
        'numeric',
        userTimezone
      );

      return { dayOfWeek: `Week ${weekNum}`, formattedDate: `${startFormatted} - ${endFormatted}` };
    }

    case AggregationGroupBy.Monthly: {
      const monthDate = localDateFromYMStart(dateStr);
      const [year] = dateStr.split('-');
      const monthName = monthDate.toLocaleDateString('en-GB', {
        month: 'long',
        ...(userTimezone && { timeZone: userTimezone }),
      });

      const startFormatted = formatDate(
        localDateFromYMD(startDate!),
        'numeric',
        'short',
        'numeric',
        userTimezone
      );

      const endFormatted = formatDate(
        localDateFromYMD(endDate!),
        'numeric',
        'short',
        'numeric',
        userTimezone
      );

      return {
        dayOfWeek: `${monthName} ${year}`,
        formattedDate: `${startFormatted} - ${endFormatted}`,
      };
    }

    case AggregationGroupBy.Quarterly: {
      const [year, quarter] = dateStr.split('-Q');

      const startFormatted = formatDate(
        localDateFromYMD(startDate!),
        'numeric',
        'short',
        'numeric',
        userTimezone
      );

      const endFormatted = formatDate(
        localDateFromYMD(endDate!),
        'numeric',
        'short',
        'numeric',
        userTimezone
      );

      return {
        dayOfWeek: `Q${quarter} ${year}`,
        formattedDate: `${startFormatted} - ${endFormatted}`,
      };
    }

    case AggregationGroupBy.Yearly: {
      const startFormatted = formatDate(
        localDateFromYMD(startDate!),
        'numeric',
        'short',
        'numeric',
        userTimezone
      );

      const endFormatted = formatDate(
        localDateFromYMD(endDate!),
        'numeric',
        'short',
        'numeric',
        userTimezone
      );

      return { dayOfWeek: dateStr, formattedDate: `${startFormatted} - ${endFormatted}` };
    }

    default: {
      const date = localDateFromYMD(dateStr);
      const dayOfWeek = date.toLocaleDateString('en-GB', {
        weekday: 'short',
        ...(userTimezone && { timeZone: userTimezone }),
      });

      return {
        dayOfWeek,
        formattedDate: formatDate(date, 'numeric', 'short', 'numeric', userTimezone),
      };
    }
  }
};
export const generateKpiKey = ({
  sectionKey,
  fieldKey,
}: {
  sectionKey: string;
  fieldKey: string;
}) => {
  return `kpi.${sectionKey}.${fieldKey}`;
};

type LogsTemplateField = NonNullable<LogsTemplate['fields']>[number];

export type KpiMergedTemplateField = LogsTemplateField & {
  sourceLogTemplateType?: LogTemplateType | null;
};

export const mergeKpiAggregatedTemplateFields = (
  worklogsTemplate: LogsTemplate | undefined | null,
  indirectPobTemplate: LogsTemplate | undefined | null,
  costLogsTemplate: LogsTemplate | undefined | null = null
): KpiMergedTemplateField[] => {
  const worklogFields =
    worklogsTemplate?.fields?.filter((field) => field.displayInLogAggregation) ?? [];
  const indirectFields =
    indirectPobTemplate?.fields?.filter((field) => field.displayInLogAggregation) ?? [];
  const costFields =
    costLogsTemplate?.fields?.filter((field) => field.displayInLogAggregation) ?? [];

  const keys = new Set<string>();
  const merged: KpiMergedTemplateField[] = [];

  for (const field of worklogFields) {
    const key = field.key;
    if (key && !keys.has(key)) {
      keys.add(key);
      merged.push({
        ...field,
        sourceLogTemplateType: LogTemplateType.WorklogTemplate,
      });
    }
  }

  for (const field of indirectFields) {
    const key = field.key;
    if (key && !keys.has(key)) {
      keys.add(key);
      merged.push({
        ...field,
        sourceLogTemplateType: LogTemplateType.IndirectPobTemplate,
      });
    }
  }

  for (const field of costFields) {
    const key = field.key;
    if (key && !keys.has(key)) {
      keys.add(key);
      merged.push({
        ...field,
        sourceLogTemplateType: LogTemplateType.CostLogTemplate,
      });
    }
  }

  return merged;
};

export function aggregatedKpiFieldValueMatchesColumn(
  field: {
    fieldKey: string;
    value: number;
    logsTemplateType?: LogTemplateType | null;
  },
  templateField: KpiMergedTemplateField
): boolean {
  if (field.fieldKey !== templateField.key) {
    return false;
  }
  if (
    field.logsTemplateType === undefined ||
    field.logsTemplateType === null ||
    templateField.sourceLogTemplateType === undefined ||
    templateField.sourceLogTemplateType === null
  ) {
    return true;
  }
  return field.logsTemplateType === templateField.sourceLogTemplateType;
}

export function getAggregatedKpiFieldNumericValue(
  fields: Array<{
    fieldKey: string;
    value: number;
    logsTemplateType?: LogTemplateType | null;
  }>,
  templateField: KpiMergedTemplateField
): number | undefined {
  const match = fields.find((field) => aggregatedKpiFieldValueMatchesColumn(field, templateField));
  return match?.value;
}

export function aggregatedKpiFieldTotalMatchesColumn(
  row: {
    fieldKey: string;
    total: number;
    logsTemplateType?: LogTemplateType | null;
  },
  templateField: KpiMergedTemplateField
): boolean {
  if (row.fieldKey !== templateField.key) {
    return false;
  }
  if (
    row.logsTemplateType === undefined ||
    row.logsTemplateType === null ||
    templateField.sourceLogTemplateType === undefined ||
    templateField.sourceLogTemplateType === null
  ) {
    return true;
  }
  return row.logsTemplateType === templateField.sourceLogTemplateType;
}
